import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Mic, ChevronRight, Square, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { TranscriptEvent } from '../lib/audioCapture'
import { DurableAudioCapture, ConnectionStatus } from '../lib/durableAudio'
import { micErrorMessage } from '../lib/micError'
import { LiveTranscript, TranscriptSegment } from '../components/encounter/LiveTranscript'
import { WaveformIndicator } from '../components/encounter/WaveformIndicator'

// Offline-started dictations use a local id ('offline-<ts>') and a locally
// generated key (kept in localStorage until synced). They buffer audio in
// IndexedDB only; once online, the dashboard syncs them to a real encounter.
const OFFLINE_KEYS_STORE = 'scribey_offline_keys'

export function getOfflineKey(encounterId: string): string | null {
  try {
    const map = JSON.parse(localStorage.getItem(OFFLINE_KEYS_STORE) || '{}')
    return map[encounterId] || null
  } catch {
    return null
  }
}

export function EncounterPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isOfflineEncounter = !!id?.startsWith('offline-')

  const [encounter, setEncounter] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Phase 2 consent gate: when a patient has explicitly DECLINED AI documentation
  // (patient_ai_consent === false), recording is hard-blocked until the doctor
  // either records a logged override (patient changed their mind, or doctor will
  // dictate manually) or backs out. Non-blocking nudge shown when consent unknown.
  const [consentOverride, setConsentOverride] = useState(false)
  const [showConsentBlock, setShowConsentBlock] = useState(false)
  const [consentReminderDismissed, setConsentReminderDismissed] = useState(false)

  // Recording state
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [audioCapture, setAudioCapture] = useState<DurableAudioCapture | null>(null)
  // Ref mirror of audioCapture — callbacks created at capture-start (e.g. the
  // voice "end note" stop_command handler) close over stale state; the ref is
  // always current. Fixes: voice stop command detected but recording never stopped.
  const audioCaptureRef = useRef<DurableAudioCapture | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const [recordingStopped, setRecordingStopped] = useState(false)
  const [finishingNote, setFinishingNote] = useState(false)
  const wakeLockRef = useRef<any>(null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('stopped')
  const [recoveringAudio, setRecoveringAudio] = useState(false)
  const [orphanedSession, setOrphanedSession] = useState<{ approxSeconds: number } | null>(null)
  const [noteGenerated, setNoteGenerated] = useState(false)
  const autoRecordTriggered = useRef(false)

  // Long press pause state
  const [pausePressProgress, setPausePressProgress] = useState(0)
  const pauseTimerRef = useRef<number | null>(null)

  // Slide to end state — use ref for smooth tracking, state for render
  const [slidePercent, setSlidePercent] = useState(0)
  const slideTrackingRef = useRef(false)
  const slideStartXRef = useRef(0)
  const slideWidthRef = useRef(0)
  const slideContainerRef = useRef<HTMLDivElement>(null)

  // Transcript state
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([])
  const [interimText, setInterimText] = useState<string>('')

  // Agent nudges state — now as a list for the panel
  const [agentMessages, setAgentMessages] = useState<Array<{ message: string; timestamp: string }>>([])
  const nudgeWsRef = useRef<WebSocket | null>(null)

  // Patient and type
  const [selectedPatient, setSelectedPatient] = useState<string>('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [savingPatient, setSavingPatient] = useState(false)
  const [patientAutoDetected, setPatientAutoDetected] = useState(false)
  const [fuzzyMatch, setFuzzyMatch] = useState<any | null>(null)
  const [fuzzyCanonical, setFuzzyCanonical] = useState('')  // chosen/edited canonical "Last, First"
  const [fuzzyDob, setFuzzyDob] = useState('')              // optional DOB correction on confirm
  // Seed the spelling editor whenever a new fuzzy candidate appears (default to existing chart spelling).
  useEffect(() => {
    if (fuzzyMatch?.candidate) {
      const c = fuzzyMatch.candidate
      setFuzzyCanonical(`${c.last_name || ''}, ${c.first_name || ''}`.replace(/^, |, $/g, ''))
      setFuzzyDob(c.dob || '')
    }
  }, [fuzzyMatch])
  const [typeAutoDetected, setTypeAutoDetected] = useState(false)
  const patientSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (id) loadEncounter()
  }, [id])

  // Check for an orphaned (crashed) recording buffer for this encounter
  useEffect(() => {
    if (!id) return
    DurableAudioCapture.findOrphanedRecordings()
      .then((orphans) => {
        const mine = orphans.find((o) => o.encounterId === id)
        if (mine) setOrphanedSession({ approxSeconds: mine.approxSeconds })
      })
      .catch(() => {})
  }, [id])

  async function handleRecoverOrphan() {
    if (!id) return
    setRecoveringAudio(true)
    try {
      const token = api.getToken()
      if (!token) throw new Error('Not authenticated')
      const { key_b64 } = await api.getRecoveryKey(id)
      await DurableAudioCapture.recoverOrphan(id, token, key_b64)
      setOrphanedSession(null)
      await loadEncounter()
    } catch (err: any) {
      setError(`Audio recovery failed: ${err.message}. Your audio is still saved — try again.`)
    } finally {
      setRecoveringAudio(false)
    }
  }

  async function handleDiscardOrphan() {
    if (!id) return
    try {
      await DurableAudioCapture.discardOrphan(id)
    } catch {}
    setOrphanedSession(null)
  }

  // Timer
  useEffect(() => {
    if (isRecording && !isPaused) {
      const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
      return () => clearInterval(interval)
    }
  }, [isRecording, isPaused])

  // Keep the screen awake while recording — phones sleeping mid-dictation
  // suspend the mic (especially iOS). Re-acquire on visibility change because
  // the lock is auto-released when the user switches apps and returns.
  useEffect(() => {
    if (!isRecording) return
    let released = false

    async function acquire() {
      try {
        if (!released && 'wakeLock' in navigator) {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen')
        }
      } catch {
        // Not supported or denied (e.g. low battery) — recording still works,
        // the user just has to keep the screen on manually.
      }
    }

    function onVisibility() {
      if (document.visibilityState === 'visible') acquire()
    }

    acquire()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      released = true
      document.removeEventListener('visibilitychange', onVisibility)
      try { wakeLockRef.current?.release() } catch {}
      wakeLockRef.current = null
    }
  }, [isRecording])

  // Periodic poll while recording to check for auto-detected patient and type
  useEffect(() => {
    if (!isRecording || isOfflineEncounter) return

    const pollInterval = setInterval(async () => {
      try {
        const updatedEncounter = await api.getEncounter(id!)
        
        // Check for auto-detected patient (only if user hasn't manually entered one)
        if (updatedEncounter.patient_name && !selectedPatient) {
          setSelectedPatient(updatedEncounter.patient_name)
          setPatientAutoDetected(true)
        }
        
        // Check for auto-detected encounter type (only if still default and user hasn't changed it)
        if (updatedEncounter.encounter_type && 
            updatedEncounter.encounter_type !== selectedType &&
            !selectedType) {
          setSelectedType(updatedEncounter.encounter_type)
          setTypeAutoDetected(true)
        }
        
        setEncounter(updatedEncounter)
      } catch (err) {
        console.warn('Failed to poll for auto-detected info:', err)
      }
    }, 10000) // Poll every 10 seconds

    return () => clearInterval(pollInterval)
  }, [isRecording, id, selectedPatient, selectedType])

  // Auto-record if ?autoRecord=true
  useEffect(() => {
    if (
      !autoRecordTriggered.current &&
      searchParams.get('autoRecord') === 'true' &&
      encounter &&
      !isRecording &&
      !recordingStopped
    ) {
      autoRecordTriggered.current = true
      handleStartRecording()
    }
  }, [encounter, searchParams])

  // Agent nudges WebSocket connection
  useEffect(() => {
    if (!isRecording || !id) {
      // Disconnect when not recording
      if (nudgeWsRef.current) {
        nudgeWsRef.current.close()
        nudgeWsRef.current = null
      }
      return
    }

    // Connect to agent nudges WebSocket
    const token = localStorage.getItem('scribey_token')
    if (!token) return

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}/api/v1/agent-nudges/encounters/${id}?token=${encodeURIComponent(token)}`
    
    try {
      const ws = new WebSocket(wsUrl)
      
      ws.onopen = () => {
        console.log('Agent nudges WebSocket connected')
      }
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          if (data.type === 'nudge') {
            // Add to agent messages list
            setAgentMessages(prev => [...prev, { message: data.message, timestamp: data.timestamp }])
            // Vibrate phone on new agent message
            if (navigator.vibrate) {
              navigator.vibrate([100, 50, 100]) // two short pulses
            }
            
            // Toast bubbles auto-display, no scroll needed
          }
        } catch (err) {
          console.error('Failed to parse nudge message:', err)
        }
      }
      
      ws.onerror = (error) => {
        console.error('Nudge WebSocket error:', error)
      }
      
      ws.onclose = () => {
        console.log('Agent nudges WebSocket closed')
      }
      
      nudgeWsRef.current = ws
      
      return () => {
        ws.close()
      }
    } catch (err) {
      console.error('Failed to connect to nudges WebSocket:', err)
    }
  }, [isRecording, id])

  // Auto-navigate to note page after recording stops (with confetti flag)
  useEffect(() => {
    if (recordingStopped) {
      if (isOfflineEncounter) {
        // No server note yet — audio is saved locally; dashboard offers sync.
        navigate('/')
      } else {
        navigate(`/encounter/${id}/note?from=recording${searchParams.get('more') === '1' ? '&regen=1' : ''}`)
      }
    }
  }, [recordingStopped])

  async function loadEncounter() {
    if (isOfflineEncounter) {
      // Local-only stub; no server round-trip while offline.
      setEncounter({ id, status: 'empty', encounter_type: null, patient_name: null })
      setLoading(false)
      return
    }
    try {
      const data = await api.getEncounter(id!)
      setEncounter(data)
      setSelectedType(data.encounter_type || '')
      // A capture started from an unlinked schedule entry passes the patient name as
      // ?patient=. If the encounter has no name yet, attach it through the same
      // fuzzy-match-aware path as manual entry (surfaces the confirm modal on a near
      // duplicate instead of silently dropping the name).
      const prefillName = searchParams.get('patient')
      if (!data.patient_name && prefillName && prefillName.trim()) {
        setSelectedPatient(prefillName)
        attachPrefilledName(prefillName.trim())
      } else {
        setSelectedPatient(data.patient_name || '')
      }
      const notes = await api.listNotes(id!)
      setNoteGenerated(notes.length > 0)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleStartRecording() {
    // Consent gate: hard-block if the patient declined AI documentation, unless
    // the doctor has explicitly chosen to override (logged in the dialog handler).
    if (encounter?.patient_ai_consent === false && !consentOverride) {
      setShowConsentBlock(true)
      return
    }
    try {
      const token = api.getToken()
      if (!token && !isOfflineEncounter) {
        setError('Not authenticated')
        return
      }

      // Encryption key for the local durability buffer: server-derived for
      // normal encounters, locally generated for offline-started ones.
      let keyB64: string
      if (isOfflineEncounter) {
        const localKey = getOfflineKey(id!)
        if (!localKey) {
          setError('Offline session key missing — please start a new capture.')
          return
        }
        keyB64 = localKey
      } else {
        const { key_b64 } = await api.getRecoveryKey(id!)
        keyB64 = key_b64
      }

      const capture = new DurableAudioCapture({
        encounterId: id!,
        token: token || '',
        recoveryKeyB64: keyB64,
        localOnly: isOfflineEncounter,
        onTranscript: handleTranscript,
        onStatus: (status) => setConnectionStatus(status),
        onError: (err) => {
          console.error('Audio capture error:', err)
          setError(err.message)
        },
        onAudioLevel: (level) => setAudioLevel(level),
      })

      await capture.start()
      audioCaptureRef.current = capture
      setAudioCapture(capture)
      setIsRecording(true)
      setElapsedSeconds(0)
      setRecordingStopped(false)
      setAgentMessages([{ message: "I'm listening — go ahead and dictate. I'll help if anything needs clarification.", timestamp: new Date().toISOString() }])
      // Auto-dismiss initial message after 4s
      setTimeout(() => setAgentMessages([]), 4000)
    } catch (err: any) {
      // Reset encounter status since recording never actually started
      try {
        if (!isOfflineEncounter) await api.updateEncounterStatus(id!, 'empty')
      } catch {}
      
      setError(micErrorMessage(err))
    }
  }

  function handleTranscript(event: TranscriptEvent) {
    if (event.type === 'error') {
      setError(event.message || 'Transcription error')
      return
    }
    if (event.type === 'stop_command') {
      // Voice command detected — auto-stop recording
      handleStopRecording()
      return
    }
    if (event.type === 'transcript' && event.text) {
      if (event.is_final) {
        setTranscriptSegments((segments) => [
          ...segments,
          { text: event.text!, speaker: event.speaker, is_final: true, timestamp: Date.now() },
        ])
        setInterimText('')
      } else {
        setInterimText(event.text)
      }
    }
  }

  async function handleStopRecording() {
    const capture = audioCaptureRef.current ?? audioCapture
    if (capture) {
      // Show "finishing…" while the server drains remaining transcription —
      // stop() now waits for the server's stop_complete (up to ~25s) so late
      // chunks transcribed after a reconnect are never cut off.
      setFinishingNote(true)
      const { delivered } = await capture.stop()
      setFinishingNote(false)
      setIsRecording(false)
      setIsPaused(false)
      setAudioLevel(0)

      if (isOfflineEncounter) {
        // Audio is sealed in the local encrypted buffer; sync happens from the
        // dashboard when connectivity returns.
        audioCaptureRef.current = null
        setAudioCapture(null)
        setConnectionStatus('stopped')
        setRecordingStopped(true)
        return
      }
      setRecordingStopped(true)

      // If the network dropped during recording, some audio never reached the
      // server — batch-upload the encrypted local buffer for transcription now.
      if (!delivered && navigator.onLine) {
        setRecoveringAudio(true)
        try {
          await capture.recoverAudio()
        } catch (err: any) {
          console.error('Audio recovery failed:', err)
          setError(
            'Some audio could not be uploaded yet — it is saved securely on this device. ' +
            'Check your connection and use the recovery banner to retry.'
          )
          setOrphanedSession({ approxSeconds: 0 })
        } finally {
          setRecoveringAudio(false)
        }
      }
      audioCaptureRef.current = null
      setAudioCapture(null)
      setConnectionStatus('stopped')

      // Update encounter status
      try {
        await api.updateEncounterStatus(id!, 'empty')
      } catch (err) {
        console.warn('Failed to update encounter status:', err)
      }
      
      // Poll for auto-detected patient and type after stopping.
      // Extraction is an async LLM call that can take 5-20s after the
      // transcript finalizes; a single early check loses the race and the
      // detected name never shows. Retry every 3s for up to 30s, stopping
      // early once both fields are resolved.
      {
        let attempts = 0
        const maxAttempts = 10
        const checkExtraction = async () => {
          attempts++
          try {
            const updatedEncounter = await api.getEncounter(id!)
            let patientDone = false
            let typeDone = false

            if (updatedEncounter.patient_name) {
              patientDone = true
              if (!selectedPatient) {
                setSelectedPatient(updatedEncounter.patient_name)
                setPatientAutoDetected(true)
              }
            }
            if (updatedEncounter.encounter_type) {
              typeDone = true
              if (updatedEncounter.encounter_type !== selectedType && !selectedType) {
                setSelectedType(updatedEncounter.encounter_type)
                setTypeAutoDetected(true)
              }
            }
            setEncounter(updatedEncounter)
            if ((patientDone && typeDone) || attempts >= maxAttempts) return
          } catch (err) {
            console.warn('Failed to check for auto-detected info:', err)
            if (attempts >= maxAttempts) return
          }
          setTimeout(checkExtraction, 3000)
        }
        setTimeout(checkExtraction, 3000)
      }
    }
  }

  // Long press pause handlers
  function handlePauseStart() {
    if (!isRecording || isPaused) return
    pauseTimerRef.current = window.setInterval(() => {
      setPausePressProgress((prev) => {
        if (prev >= 100) {
          handlePauseComplete()
          return 100
        }
        return prev + 10
      })
    }, 50)
  }

  function handlePauseEnd() {
    if (pauseTimerRef.current) {
      clearInterval(pauseTimerRef.current)
      pauseTimerRef.current = null
    }
    setPausePressProgress(0)
  }

  function handlePauseComplete() {
    handlePauseEnd()
    if (audioCapture && isRecording && !isPaused) {
      audioCapture.pause()
      setIsPaused(true)
    }
  }

  function handleResume() {
    if (audioCapture && isPaused) {
      audioCapture.resume()
      setIsPaused(false)
    }
  }

  // Attach a name handed in via ?patient= (from a schedule capture). Mirrors
  // handlePatientChange's error handling: a 409 fuzzy-match opens the confirm modal.
  async function attachPrefilledName(name: string) {
    try {
      await api.updateEncounter(id!, { patient_name: name })
      setFuzzyMatch(null)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.detail?.code === 'fuzzy_patient_match') {
        setFuzzyMatch(err.detail)
      } else {
        console.warn('Failed to attach scheduled patient name:', err)
      }
    }
  }

  // Auto-save patient name with debounce
  function handlePatientChange(name: string) {
    setSelectedPatient(name)
    setPatientAutoDetected(false) // User is manually editing, clear auto-detected flag
    if (patientSaveTimeoutRef.current) clearTimeout(patientSaveTimeoutRef.current)
    if (name.trim()) {
      patientSaveTimeoutRef.current = setTimeout(async () => {
        setSavingPatient(true)
        try {
          await api.updateEncounter(id!, { patient_name: name })
          setFuzzyMatch(null)
        } catch (err) {
          if (err instanceof ApiError && err.status === 409 && err.detail?.code === 'fuzzy_patient_match') {
            // Don't auto-merge — ask the doctor to confirm or reject.
            setFuzzyMatch(err.detail)
          } else {
            console.warn('Failed to save patient name:', err)
          }
        } finally {
          setSavingPatient(false)
        }
      }, 1000) // 1s debounce
    }
  }

  // Doctor confirmed the fuzzy candidate is the same patient — link to it.
  async function confirmFuzzyMatch() {
    if (!fuzzyMatch?.candidate?.patient_id) return
    setSavingPatient(true)
    try {
      const payload: any = { confirm_patient_id: fuzzyMatch.candidate.patient_id }
      // Send corrected spelling only if the doctor changed it from the existing chart name.
      const c = fuzzyMatch.candidate
      const existing = `${c.last_name || ''}, ${c.first_name || ''}`.replace(/^, |, $/g, '')
      if (fuzzyCanonical.trim() && fuzzyCanonical.trim() !== existing) {
        payload.canonical_name = fuzzyCanonical.trim()
      }
      if (fuzzyDob.trim()) payload.patient_dob = fuzzyDob.trim()
      await api.updateEncounter(id!, payload)
      const displayName = (payload.canonical_name || existing)
      setSelectedPatient(displayName)
      setFuzzyMatch(null)
    } catch (err) {
      console.warn('Failed to confirm patient match:', err)
    } finally {
      setSavingPatient(false)
    }
  }

  // Doctor rejected the match — create a brand-new patient with the typed name.
  async function rejectFuzzyMatch() {
    if (!fuzzyMatch?.typed_name) { setFuzzyMatch(null); return }
    const { first_name, last_name } = fuzzyMatch.typed_name
    setSavingPatient(true)
    try {
      await api.updateEncounter(id!, { patient_name: `${first_name} ${last_name}`, force_new_patient: true })
      setFuzzyMatch(null)
    } catch (err) {
      console.warn('Failed to create new patient:', err)
    } finally {
      setSavingPatient(false)
    }
  }

  // Doctor chose to override a consent decline (patient changed mind / will note manually).
  // Logged server-side via audit, then proceed to record.
  async function overrideConsentAndRecord() {
    try {
      await api.updateEncounter(id!, { ai_consent_override: true })
    } catch (err) {
      console.warn('Failed to log consent override:', err)
    }
    setConsentOverride(true)
    setShowConsentBlock(false)
    // proceed to record now that override is set
    setTimeout(() => handleStartRecording(), 0)
  }

  // Handle navigating away — clean up recording and set proper status
  async function handleBack() {
    if (isRecording && audioCapture) {
      // Stop the recording cleanly
      await audioCapture.stop()
      setAudioCapture(null)
      setIsRecording(false)
      setIsPaused(false)
      setAudioLevel(0)
    }
    // Always try to mark as empty unless encounter is already in a terminal state
    // This catches: recording (active or abandoned), processing (stuck), empty stays empty
    const terminalStatuses = ['draft', 'final', 'error']
    const currentStatus = encounter?.status
    if (id && !isOfflineEncounter && (!currentStatus || !terminalStatuses.includes(currentStatus))) {
      try {
        await api.updateEncounterStatus(id, 'empty')
      } catch (err) {
        console.warn('Failed to update encounter status on back:', err)
      }
    }
    navigate('/')
  }

  async function handleTypeChange(type: string) {
    setSelectedType(type)
    setTypeAutoDetected(false) // User is manually changing, clear auto-detected flag
    try {
      await api.updateEncounter(id!, { encounter_type: type })
    } catch (err) {
      console.warn('Failed to save encounter type:', err)
    }
  }

  async function handleResumeRecording() {
    setNoteGenerated(false)
    setRecordingStopped(false)
    await handleStartRecording()
  }

  // ============================================================
  // Slide-to-end: smooth, non-jittery implementation
  // Uses pointer events for unified mouse+touch handling
  // ============================================================
  const handleSlidePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isRecording) return
      const container = slideContainerRef.current
      if (!container) return

      // Capture pointer for smooth tracking outside element
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)

      slideTrackingRef.current = true
      slideStartXRef.current = e.clientX
      slideWidthRef.current = container.getBoundingClientRect().width - 64 // handle width offset
      setSlidePercent(0)
    },
    [isRecording]
  )

  const handleSlidePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!slideTrackingRef.current) return
      const dx = e.clientX - slideStartXRef.current
      const pct = Math.max(0, Math.min(100, (dx / slideWidthRef.current) * 100))
      setSlidePercent(pct)
    },
    []
  )

  const handleSlidePointerUp = useCallback(() => {
    if (!slideTrackingRef.current) return
    slideTrackingRef.current = false

    if (slidePercent >= 80) {
      // Trigger stop
      setSlidePercent(100)
      handleStopRecording()
    } else {
      setSlidePercent(0)
    }
  }, [slidePercent])

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-pulse text-brand-500 text-lg">Loading encounter...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-6 text-center">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <button onClick={() => navigate('/')} className="mt-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline">
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!encounter) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">Encounter not found</p>
          <button onClick={() => navigate('/')} className="mt-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline">
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {selectedType === 'office' ? 'Office Visit' : 
           selectedType === 'op_note' ? 'Op Note' : 
           selectedType === 'consult' ? 'Consult' : 
           selectedType === 'inpatient' ? 'Rounding' : 
           selectedType === 'procedure' ? 'Procedure' : 
           selectedType === 'telehealth' ? 'Telehealth' : 'New Visit'}
        </h1>
      </div>

      {/* Orphaned recording recovery banner */}
      {orphanedSession && !isRecording && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            You have an unfinished dictation saved on this device
            {orphanedSession.approxSeconds > 0 ? ` (~${orphanedSession.approxSeconds}s of audio)` : ''}.
          </p>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleRecoverOrphan}
              disabled={recoveringAudio}
              className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
            >
              {recoveringAudio ? 'Transcribing…' : 'Transcribe it'}
            </button>
            <button
              onClick={handleDiscardOrphan}
              disabled={recoveringAudio}
              className="text-sm px-3 py-1.5 rounded-lg border border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Finishing — server is draining the last transcription chunks */}
      {finishingNote && (
        <div className="mb-4 flex items-center gap-2 bg-brand-600/10 border border-brand-600/40 rounded-lg px-4 py-3">
          <div className="w-3 h-3 rounded-full bg-brand-500 animate-pulse" />
          <p className="text-sm text-gray-700 dark:text-gray-300">Finishing transcription… capturing the last few seconds.</p>
        </div>
      )}

      {/* Offline dictation notice */}
      {isOfflineEncounter && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700 rounded-lg px-4 py-3">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Offline mode — audio is being saved securely on this device. It will be transcribed when you're back online.
          </p>
        </div>
      )}

      {/* Uploading saved audio after an offline recording */}
      {recoveringAudio && !orphanedSession && (
        <div className="mb-4 flex items-center gap-2 bg-brand-600/10 border border-brand-600/40 rounded-lg px-4 py-3">
          <div className="w-3 h-3 rounded-full bg-brand-500 animate-pulse" />
          <p className="text-sm text-gray-700 dark:text-gray-300">Uploading saved audio for transcription… don't close this page.</p>
        </div>
      )}

      {/* Patient and Type Selection — always editable */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1">
            Patient (optional)
            {savingPatient && <span className="ml-1 text-brand-400">saving...</span>}
            {patientAutoDetected && !savingPatient && (
              <span className="ml-1 text-emerald-400">✓ auto-detected</span>
            )}
          </label>
          <input
            type="text"
            value={selectedPatient}
            onChange={(e) => handlePatientChange(e.target.value)}
            placeholder="Last, First"
            className={`w-full bg-gray-100 dark:bg-gray-900 border rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              patientAutoDetected ? 'border-emerald-500/50' : 'border-gray-200 dark:border-gray-800'
            }`}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1">
            Visit Type
            {typeAutoDetected && (
              <span className="ml-1 text-emerald-400">✓ auto-detected</span>
            )}
          </label>
          <select
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
            className={`w-full bg-gray-100 dark:bg-gray-900 border rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              typeAutoDetected ? 'border-emerald-500/50' : 'border-gray-200 dark:border-gray-800'
            }`}
          >
            <option value="">Auto-detect</option>
            <option value="office">Office Visit</option>
            <option value="op_note">Op Note</option>
            <option value="consult">Consult</option>
            <option value="inpatient">Rounding</option>
            <option value="procedure">Procedure</option>
            <option value="telehealth">Telehealth</option>
          </select>
        </div>
      </div>

      {/* Consent reminder — non-blocking. Shows whenever this patient has no AI
          consent on file (null/unknown), in BOTH the pre-record and recording
          states, so it also covers the Capture/autoRecord flow that skips the
          big record button. Dismissible. Hidden when consent is on file or declined
          (decline is hard-gated separately). */}
      {encounter?.patient_id && (encounter?.patient_ai_consent === null || encounter?.patient_ai_consent === undefined) && !consentReminderDismissed && !recordingStopped && (
        <div className="flex items-start gap-2 bg-sky-50 dark:bg-sky-900/15 border border-sky-200 dark:border-sky-800 rounded-xl px-4 py-3 mb-4">
          <span className="text-sky-500 mt-0.5 flex-shrink-0">ℹ️</span>
          <p className="text-sm text-sky-800 dark:text-sky-300 leading-relaxed flex-1">
            No AI documentation consent on file for this patient. Please let them know you’re using an AI assistant for notes and confirm they’re okay with it — we’ll capture it automatically if it’s mentioned during the visit.
          </p>
          <button onClick={() => setConsentReminderDismissed(true)} className="text-sky-400 hover:text-sky-600 dark:hover:text-sky-200 flex-shrink-0" title="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Recording Controls */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-6">
        {/* NOT RECORDING: Big button (hide during autoRecord transition) */}
        {!isRecording && !recordingStopped && !searchParams.get('autoRecord') && (
          <>
            <div className="flex justify-center mb-4">
              <button
                onClick={handleStartRecording}
                className="w-20 h-20 rounded-full bg-brand-600 hover:bg-brand-500 text-white flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
              >
                <Mic size={32} />
              </button>
            </div>
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">Tap to start recording</p>
            </div>
          </>
        )}

        {/* Post-recording: immediately navigates to note page via useEffect */}

        {/* AUTO-RECORD PENDING: Show loading spinner or error */}
        {!isRecording && searchParams.get('autoRecord') && !recordingStopped && (
          <div className="flex flex-col items-center py-4">
            {error ? (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <Mic size={28} className="text-red-500" />
                </div>
                <p className="text-red-600 dark:text-red-400 text-sm text-center max-w-sm mb-4">{error}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setError(null); autoRecordTriggered.current = false; handleStartRecording() }}
                    className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm"
                  >
                    Back to Dashboard
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 border-3 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">Connecting microphone...</p>
              </>
            )}
          </div>
        )}

        {/* RECORDING: Compact bar */}
        {isRecording && (
          <>
            {isPaused ? (
              // Paused state: show resume button
              <button
                onClick={handleResume}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-yellow-600/20 border border-yellow-600 rounded-lg hover:bg-yellow-600/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-lg font-mono text-yellow-500">{formatTime(elapsedSeconds)}</span>
                  <span className="text-xs text-yellow-500 font-medium px-2 py-1 bg-yellow-900/40 rounded">PAUSED</span>
                </div>
                <span className="text-sm text-yellow-400">Tap to resume</span>
              </button>
            ) : (
              // Recording: long-press bar
              <div
                onMouseDown={handlePauseStart}
                onMouseUp={handlePauseEnd}
                onMouseLeave={handlePauseEnd}
                onTouchStart={handlePauseStart}
                onTouchEnd={handlePauseEnd}
                className="relative w-full flex items-center justify-between gap-3 px-4 py-3 bg-brand-600/20 border border-brand-600 rounded-lg cursor-pointer select-none overflow-hidden"
              >
                {/* Progress overlay for long press */}
                <div
                  className="absolute inset-0 bg-brand-600/30 transition-all"
                  style={{
                    width: `${pausePressProgress}%`,
                  }}
                />
                
                <div className="relative flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-lg font-mono text-brand-400">{formatTime(elapsedSeconds)}</span>
                  <div className="flex-shrink-0">
                    <WaveformIndicator level={audioLevel} isActive={true} />
                  </div>
                  {/* Connection truth chip — audio is always saved locally first */}
                  {connectionStatus === 'live' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 font-medium">● Live</span>
                  )}
                  {connectionStatus === 'buffering' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500 font-medium">● Buffering — will catch up</span>
                  )}
                  {connectionStatus === 'offline' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-500 font-medium">● Offline — saved locally</span>
                  )}
                </div>
                
                <span className="relative text-sm text-gray-400">Long press to pause</span>
              </div>
            )}

            {/* Agent message — inline below recording bar */}
            {agentMessages.length > 0 && (
              <div className="mt-3 flex items-start gap-2 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 animate-slide-up">
                <span className="text-sm flex-shrink-0">🤖</span>
                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{agentMessages[agentMessages.length - 1].message}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Live Transcript */}
      <div className="mb-4">
        <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
          Live Transcript
        </h2>
        <LiveTranscript segments={transcriptSegments} interimText={interimText} />
      </div>

      {/* Post-recording: immediately navigates to note page via useEffect */}

      {/* Slide to End — fixed at bottom during recording */}
      {isRecording && (
        <div className="fixed bottom-0 left-0 right-0 z-50 px-8 md:px-4 pb-4 pt-2 bg-gradient-to-t from-white via-white/95 dark:from-gray-950 dark:via-gray-950/95 to-transparent">
          <div
            ref={slideContainerRef}
            className="relative h-10 bg-gray-100 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-full overflow-hidden touch-none max-w-[16rem] md:max-w-xs mx-auto"
          >
            {/* Progress fill */}
            <div
              className="absolute inset-y-0 left-0 bg-red-500/20 rounded-full"
              style={{
                width: `${slidePercent}%`,
                transition: slideTrackingRef.current ? 'none' : 'width 300ms ease-out',
              }}
            />

            {/* Sliding handle */}
            <div
              className="absolute left-0.5 top-0.5 bottom-0.5 w-10 bg-red-600 rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing z-10"
              style={{
                transform: `translateX(${(slidePercent / 100) * (slideWidthRef.current || 200)}px)`,
                transition: slideTrackingRef.current ? 'none' : 'transform 300ms ease-out',
              }}
              onPointerDown={handleSlidePointerDown}
              onPointerMove={handleSlidePointerMove}
              onPointerUp={handleSlidePointerUp}
              onPointerCancel={handleSlidePointerUp}
            >
              <Square size={12} className="text-white" />
            </div>

            {/* Label */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                Slide to end recording →
              </span>
            </div>

            {/* Animated chevrons */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-0.5 pointer-events-none">
              <ChevronRight size={12} className="text-gray-300 dark:text-gray-600 animate-pulse" />
              <ChevronRight
                size={12}
                className="text-gray-300 dark:text-gray-600 animate-pulse"
                style={{ animationDelay: '150ms' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Resume button after note generation */}
      {noteGenerated && recordingStopped && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <button
            onClick={handleResumeRecording}
            className="flex items-center gap-3 bg-brand-600 hover:bg-brand-500 text-white px-6 py-3 rounded-full font-medium shadow-2xl transition-all hover:scale-105 active:scale-95"
          >
            <Mic size={20} />
            Resume
          </button>
        </div>
      )}

      {/* Consent block: patient declined AI documentation. Hard-stop with override. */}
      {showConsentBlock && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-amber-300 dark:border-amber-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-amber-600 text-sm font-bold">!</span>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Patient declined AI documentation</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This patient has opted out of AI-assisted documentation. You can record only if they have changed their mind, or you intend to dictate this encounter manually. Your choice to proceed will be logged.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={overrideConsentAndRecord}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                Proceed anyway (logged override)
              </button>
              <button
                onClick={() => setShowConsentBlock(false)}
                className="w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fuzzy patient-match confirmation — never auto-merge charts */}
      {fuzzyMatch && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Is this the same patient?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              The name you entered is close to an existing chart. Confirm only if it is the same person — this links their prior history.
            </p>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800 mb-3 text-sm">
              {fuzzyMatch.candidate?.dob && (
                <div className="flex justify-between px-3 py-2">
                  <span className="text-gray-400">Existing chart DOB</span>
                  <span className="text-gray-700 dark:text-gray-300">{fuzzyMatch.candidate.dob}</span>
                </div>
              )}
              {fuzzyMatch.candidate?.last_visit && (
                <div className="flex justify-between px-3 py-2">
                  <span className="text-gray-400">Last visit</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {new Date(fuzzyMatch.candidate.last_visit).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Spelling picker/editor — choose which spelling is canonical, or type a correction */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Correct spelling for this patient</p>
              <div className="space-y-1.5">
                {(() => {
                  const c = fuzzyMatch.candidate || {}
                  const t = fuzzyMatch.typed_name || {}
                  const existing = `${c.last_name || ''}, ${c.first_name || ''}`.replace(/^, |, $/g, '')
                  const typed = `${t.last_name || ''}, ${t.first_name || ''}`.replace(/^, |, $/g, '')
                  const opts = [
                    { label: 'Existing chart', val: existing },
                    ...(typed && typed !== existing ? [{ label: 'What you typed', val: typed }] : []),
                  ]
                  return opts.map((o) => (
                    <label key={o.val} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="canonicalName"
                        checked={fuzzyCanonical.trim() === o.val}
                        onChange={() => setFuzzyCanonical(o.val)}
                        className="accent-brand-600"
                      />
                      <span className="text-gray-900 dark:text-gray-100 font-medium">{o.val}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{o.label}</span>
                    </label>
                  ))
                })()}
              </div>
              <input
                type="text"
                value={fuzzyCanonical}
                onChange={(e) => setFuzzyCanonical(e.target.value)}
                placeholder="Last, First"
                className="mt-2 w-full bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <input
                type="date"
                value={fuzzyDob}
                onChange={(e) => setFuzzyDob(e.target.value)}
                className="mt-2 w-full bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-[10px] text-gray-400 mt-1">Confirming updates the patient chart to this spelling{fuzzyDob ? ' and DOB' : ''}.</p>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={confirmFuzzyMatch}
                disabled={savingPatient}
                className="w-full bg-brand-600 hover:bg-brand-500 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                Yes — same patient, link history
              </button>
              <button
                onClick={rejectFuzzyMatch}
                disabled={savingPatient}
                className="w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                No — create a new patient
              </button>
              <button
                onClick={() => setFuzzyMatch(null)}
                disabled={savingPatient}
                className="w-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-1.5 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
